import React, { useState, useEffect } from 'react';
import { XMarkIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { modelService } from '../../services/api';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReactMarkdown from 'react-markdown';
import Editor from '@monaco-editor/react';

// Map phase names to phase numbers for rebuild API
const PHASE_NAME_TO_NUMBER = {
  '01_analyze_data': 1,
  '02_prepare_data': 2,
  '03_build_baselines': 3,
  '04_search_models': 4,
  '05_evaluate_final': 5,
  '06_package_final_model': 6,
};

function CheckpointInspector({ modelId, phase, phaseName, onClose }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    reports: true,
    code: true,
    artifacts: true
  });
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [feedback, setFeedback] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  const [editingCode, setEditingCode] = useState({}); // Track which code blocks are being edited
  const [editedCode, setEditedCode] = useState({}); // Store edited code content
  const [applyingCode, setApplyingCode] = useState(false);

  useEffect(() => {
    loadCheckpointDetails();
  }, [modelId, phase]);

  const loadCheckpointDetails = async () => {
    try {
      setLoading(true);
      const data = await modelService.getCheckpointDetails(modelId, phase);
      setDetails(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching checkpoint details for', modelId, phase, ':', err);
      // Don't block the UI - still allow feedback submission
      setError('Checkpoint details could not be loaded. You can still provide feedback below.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!feedback.trim()) {
      setFeedbackError('Please enter some feedback');
      return;
    }

    try {
      setSubmittingFeedback(true);
      setFeedbackError(null);

      // Extract experiment_id from modelId (format: modelName/experimentId)
      const experimentId = modelId.includes('/') ? modelId.split('/')[1] : modelId;

      // Match backend's FeedbackSubmission schema (plexe-agent/src/api/ml_models/router.py:391-395)
      await modelService.submitFeedback(experimentId, phase, {
        approved: true,              // User submitting feedback = approval
        comments: feedback.trim(),   // User's feedback text goes in 'comments' field
        requested_changes: [],       // Empty for now (could be expanded for structured changes)
        enable_auto_mode: false      // Keep pausing at each checkpoint (default behavior)
      });

      setFeedbackSuccess(true);
      setFeedback('');

      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setFeedbackError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleEditCode = (codeKey, currentCode) => {
    setEditingCode(prev => ({ ...prev, [codeKey]: true }));
    setEditedCode(prev => ({ ...prev, [codeKey]: currentCode }));
  };

  const handleCancelEdit = (codeKey) => {
    setEditingCode(prev => {
      const newState = { ...prev };
      delete newState[codeKey];
      return newState;
    });
    setEditedCode(prev => {
      const newState = { ...prev };
      delete newState[codeKey];
      return newState;
    });
  };

  const handleApplyCodeChanges = async (codeKey) => {
    try {
      setApplyingCode(true);

      // Extract experiment_id from modelId
      const experimentId = modelId.includes('/') ? modelId.split('/')[1] : modelId;

      // Convert phase name to phase number for rebuild API
      const phaseNumber = PHASE_NAME_TO_NUMBER[phase];
      if (!phaseNumber) {
        throw new Error(`Unknown phase: ${phase}`);
      }

      // Note: updateCheckpoint is not implemented yet (returns 501)
      // Skip for now - checkpoints will be deleted by rebuild
      // await modelService.updateCheckpoint(experimentId, phase, codeKey, editedCode[codeKey]);

      // Rebuild the model from this phase with the updated code
      // Backend will delete checkpoints >= phaseNumber and restart from there
      await modelService.rebuildModel(experimentId, { resume_from_phase: phaseNumber });

      // Clear edit state
      handleCancelEdit(codeKey);

      // Close the modal and let the user see the rebuild progress
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err) {
      console.error('Error applying code changes:', err);
      alert('Failed to apply code changes. Please try again.');
    } finally {
      setApplyingCode(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleItem = (itemKey) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemKey)) {
        next.delete(itemKey);
      } else {
        next.add(itemKey);
      }
      return next;
    });
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const formatReportContent = (report) => {
    if (report.format === 'json') {
      return JSON.stringify(report.content, null, 2);
    }
    return report.content;
  };

  const getReportBorderColor = (type) => {
    switch (type) {
      case 'performance':
        return 'border-emerald-500 dark:border-emerald-600';
      case 'analysis':
        return 'border-blue-500 dark:border-blue-600';
      case 'configuration':
        return 'border-amber-500 dark:border-amber-600';
      default:
        return 'border-gray-300 dark:border-gray-600';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-900/75 dark:bg-gray-900/90 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/50">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-light text-gray-900 dark:text-white">
                  {phaseName} Checkpoint
                </h2>
                {details && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-mono">
                    {formatTimestamp(details.timestamp)}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {loading && (
              <div className="text-center py-12">
                <div className="animate-spin h-8 w-8 mx-auto border-b-2 border-blue-600 dark:border-blue-500 rounded-full" />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading...</p>
              </div>
            )}

            {error && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-amber-800 dark:text-amber-200">{error}</p>
              </div>
            )}

            {details && !loading && (
              <div className="space-y-6">
                {/* Reports */}
                {details.reports && details.reports.length > 0 && (
                  <CollapsibleSection
                    title="Reports"
                    count={details.reports.length}
                    isExpanded={expandedSections.reports}
                    onToggle={() => toggleSection('reports')}
                  >
                    <div className="space-y-4">
                      {details.reports.map((report, idx) => {
                        const itemKey = `report_${report.name}`;
                        const isExpanded = expandedItems.has(itemKey);

                        return (
                          <div key={idx} className={`border-l-2 ${getReportBorderColor(report.type)} pl-4`}>
                            <button
                              onClick={() => toggleItem(itemKey)}
                              className="flex items-baseline justify-between w-full mb-2 group"
                            >
                              <div className="flex items-center gap-2">
                                {isExpanded ? (
                                  <ChevronDownIcon className="w-3 h-3 text-gray-400" />
                                ) : (
                                  <ChevronRightIcon className="w-3 h-3 text-gray-400" />
                                )}
                                <span className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors">
                                  {report.title}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {report.type}
                              </span>
                            </button>

                            {isExpanded && (
                              <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded border border-gray-200 dark:border-gray-700">
                                {report.format === 'json' ? (
                                  <SyntaxHighlighter
                                    language="json"
                                    style={vscDarkPlus}
                                    customStyle={{
                                      margin: 0,
                                      padding: '0.75rem',
                                      fontSize: '0.75rem',
                                      maxHeight: '400px',
                                      overflow: 'auto',
                                      background: 'transparent'
                                    }}
                                  >
                                    {formatReportContent(report)}
                                  </SyntaxHighlighter>
                                ) : report.format === 'markdown' ? (
                                  <div className="prose prose-sm dark:prose-invert max-w-none prose-headings:font-semibold prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-li:text-gray-700 dark:prose-li:text-gray-300">
                                    <ReactMarkdown>{report.content}</ReactMarkdown>
                                  </div>
                                ) : (
                                  <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono">
                                    {report.content}
                                  </pre>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleSection>
                )}

                {/* Code */}
                {details.code && details.code.length > 0 && (
                  <CollapsibleSection
                    title="Code"
                    count={details.code.length}
                    isExpanded={expandedSections.code}
                    onToggle={() => toggleSection('code')}
                  >
                    <div className="space-y-3">
                      {details.code.map((codeItem, idx) => {
                        const itemKey = `code_${codeItem.name}`;
                        const isExpanded = expandedItems.has(itemKey);
                        const isEditing = editingCode[itemKey];
                        const currentCode = isEditing ? editedCode[itemKey] : codeItem.content;

                        return (
                          <div key={idx} className="border-l-2 border-blue-500 dark:border-blue-600 pl-4">
                            <div className="flex items-baseline justify-between w-full mb-2">
                              <button
                                onClick={() => toggleItem(itemKey)}
                                className="flex items-center gap-2 group flex-1"
                              >
                                {isExpanded ? (
                                  <ChevronDownIcon className="w-3 h-3 text-gray-400" />
                                ) : (
                                  <ChevronRightIcon className="w-3 h-3 text-gray-400" />
                                )}
                                <span className="text-sm font-medium text-gray-900 dark:text-white font-mono group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                  {codeItem.title}
                                </span>
                              </button>
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                  {codeItem.line_count} lines
                                </span>
                                {isExpanded && !isEditing && (
                                  <button
                                    onClick={() => handleEditCode(itemKey, codeItem.content)}
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors border-b border-blue-400 dark:border-blue-500 hover:border-blue-700 dark:hover:border-blue-300 pb-0.5"
                                  >
                                    Edit
                                  </button>
                                )}
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="rounded border border-gray-200 dark:border-gray-700 overflow-hidden">
                                {isEditing ? (
                                  <div className="bg-slate-900">
                                    <Editor
                                      height="500px"
                                      language={codeItem.language || "python"}
                                      theme="vs-dark"
                                      value={currentCode}
                                      onChange={(value) => setEditedCode(prev => ({ ...prev, [itemKey]: value }))}
                                      options={{
                                        minimap: { enabled: false },
                                        fontSize: 13,
                                        lineNumbers: 'on',
                                        scrollBeyondLastLine: false,
                                        automaticLayout: true,
                                        tabSize: 4,
                                        wordWrap: 'on',
                                        wrappingIndent: 'indent',
                                      }}
                                    />
                                    <div className="px-3 py-2 bg-gray-800 border-t border-gray-700 flex items-center justify-between">
                                      <span className="text-xs text-gray-400">
                                        Changes will update checkpoint and rebuild from this phase
                                      </span>
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={() => handleCancelEdit(itemKey)}
                                          disabled={applyingCode}
                                          className="
                                            px-3 py-1
                                            text-xs font-medium
                                            text-gray-300 hover:text-white
                                            transition-colors
                                            disabled:opacity-50
                                          "
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          onClick={() => handleApplyCodeChanges(itemKey)}
                                          disabled={applyingCode}
                                          className="
                                            px-3 py-1
                                            text-xs font-medium
                                            text-white
                                            bg-blue-600 hover:bg-blue-700
                                            rounded
                                            transition-colors
                                            disabled:opacity-50 disabled:cursor-not-allowed
                                            flex items-center gap-1.5
                                          "
                                        >
                                          {applyingCode ? (
                                            <>
                                              <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                              </svg>
                                              Applying...
                                            </>
                                          ) : (
                                            <>
                                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                              </svg>
                                              Apply & Rebuild
                                            </>
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <SyntaxHighlighter
                                    language={codeItem.language}
                                    style={vscDarkPlus}
                                    customStyle={{
                                      margin: 0,
                                      padding: '0.75rem',
                                      fontSize: '0.75rem',
                                      maxHeight: '600px',
                                      overflow: 'auto'
                                    }}
                                    showLineNumbers
                                  >
                                    {codeItem.content}
                                  </SyntaxHighlighter>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleSection>
                )}

                {/* Artifacts */}
                {details.artifacts && details.artifacts.length > 0 && (
                  <CollapsibleSection
                    title="Artifacts"
                    count={details.artifacts.length}
                    isExpanded={expandedSections.artifacts}
                    onToggle={() => toggleSection('artifacts')}
                  >
                    <div className="space-y-1">
                      {details.artifacts.map((artifact, idx) => (
                        <div key={idx} className="flex items-baseline justify-between py-1.5 px-3 rounded hover:bg-gray-50 dark:hover:bg-slate-700/30">
                          <span className="text-sm text-gray-700 dark:text-gray-300 font-mono">
                            {artifact.name}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                            {formatBytes(artifact.size_bytes)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CollapsibleSection>
                )}

                {/* Empty State */}
                {(!details.reports || details.reports.length === 0) &&
                 (!details.code || details.code.length === 0) &&
                 (!details.artifacts || details.artifacts.length === 0) && (
                  <div className="text-center py-12">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No checkpoint data available for this phase
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Feedback Section - Always show */}
            {!loading && (
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-baseline justify-between mb-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                    Provide Feedback
                  </h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Optional — guide the model's next steps
                  </span>
                </div>

                {feedbackSuccess ? (
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-6">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-emerald-900 dark:text-emerald-100">
                          Feedback submitted successfully
                        </p>
                        <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-1">
                          The model will continue building with your guidance.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="
                    bg-white dark:bg-slate-900
                    border border-gray-200 dark:border-gray-700
                    rounded-lg
                    shadow-sm
                    overflow-hidden
                  ">
                    {/* Canvas-style editor */}
                    <div className="p-4 bg-gray-50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        <span>Feedback Canvas</span>
                      </div>
                    </div>

                    <div className="p-1">
                      <textarea
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder="Share your thoughts on this phase...

Examples:
• Adjust feature engineering approach
• Use different model architectures
• Change hyperparameter ranges
• Focus on specific metrics"
                        className="
                          w-full h-48 px-4 py-4
                          bg-transparent
                          border-0
                          text-gray-900 dark:text-white
                          placeholder-gray-400 dark:placeholder-gray-500
                          focus:outline-none focus:ring-0
                          text-sm leading-relaxed
                          resize-none
                          font-sans
                        "
                        disabled={submittingFeedback}
                      />
                    </div>

                    {feedbackError && (
                      <div className="px-4 py-3 bg-rose-50 dark:bg-rose-900/20 border-t border-rose-200 dark:border-rose-800">
                        <p className="text-xs text-rose-600 dark:text-rose-400">{feedbackError}</p>
                      </div>
                    )}

                    <div className="px-4 py-3 bg-gray-50 dark:bg-slate-800/50 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Your feedback will guide the model building process</span>
                      </div>
                      <button
                        onClick={handleSubmitFeedback}
                        disabled={submittingFeedback || !feedback.trim()}
                        className="
                          px-4 py-1.5
                          text-xs font-medium uppercase tracking-wider
                          text-white
                          bg-blue-600 hover:bg-blue-700
                          dark:bg-blue-500 dark:hover:bg-blue-600
                          rounded
                          transition-colors
                          disabled:opacity-50 disabled:cursor-not-allowed
                          flex items-center gap-2
                        "
                      >
                        {submittingFeedback ? (
                          <>
                            <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                            Send
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/50">
            <div className="flex items-center justify-end">
              <button
                onClick={onClose}
                className="
                  text-xs font-medium uppercase tracking-wider
                  text-gray-700 dark:text-gray-300
                  hover:text-gray-900 dark:hover:text-white
                  transition-colors
                  border-b border-gray-400 dark:border-gray-500
                  hover:border-gray-900 dark:hover:border-white
                  pb-0.5
                "
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Collapsible section component
function CollapsibleSection({ title, count, isExpanded, onToggle, children }) {
  return (
    <div>
      <button
        onClick={onToggle}
        className="flex items-center gap-2 w-full text-left group mb-3"
      >
        {isExpanded ? (
          <ChevronDownIcon className="w-4 h-4 text-gray-400 transition-transform" />
        ) : (
          <ChevronRightIcon className="w-4 h-4 text-gray-400 transition-transform" />
        )}
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
          {title}
        </span>
        <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">
          ({count})
        </span>
      </button>

      {isExpanded && (
        <div className="ml-6">
          {children}
        </div>
      )}
    </div>
  );
}

export default CheckpointInspector;